/**
 * Тайминги паттерна typewriter из дизайн-системы (нода 2583-4715, компонент Inputs).
 * Скорость задана режимом `rate`: это интервал на символ, а не длительность всей строки,
 * поэтому длинный текст набирается дольше короткого — ~17 символов в секунду.
 */
export const TYPING_SPEED_MS = 60;
/** Стирание быстрее набора — дефолт ДС, typingSpeed × 0.66. */
export const ERASING_SPEED_MS = 40;
/** Пауза с набранным текстом. В ДС по умолчанию 2s, для поисковой строки уменьшена до 1s. */
export const DELAY_BEFORE_ERASE_MS = 1000;
/** Пауза с пустым полем перед набором следующего текста. */
export const DELAY_BEFORE_NEXT_MS = 300;

export type TTypewriterPhase = 'typing' | 'erasing';

export interface ITypewriterState {
  phase: TTypewriterPhase;
  /** Индекс плейсхолдера в массиве. */
  index: number;
  /** Сколько символов этого плейсхолдера показано. */
  length: number;
}

export interface ITypewriterStep {
  /** Сколько держать текущее состояние, прежде чем перейти к следующему. */
  delayMs: number;
  state: ITypewriterState;
  /** Текст следующего состояния — чтобы вызывающему коду не пересчитывать его самому. */
  text: string;
}

/**
 * Стартовое состояние: первый плейсхолдер уже показан целиком. Так на сервере и до гидрации
 * в поле есть осмысленный текст, а цикл начинается не с набора, а с паузы перед стиранием.
 */
export function getInitialTypewriterState(placeholders: string[]): ITypewriterState {
  return { phase: 'typing', index: 0, length: placeholders[0]?.length ?? 0 };
}

/** Текст, который видно в этом состоянии. */
export function getTypewriterText(placeholders: string[], { index, length }: ITypewriterState): string {
  return (placeholders[index] ?? '').slice(0, length);
}

/**
 * Следующий шаг перебора: сколько ждать в текущем состоянии и что показать после.
 *
 * Фазы идут по кругу: набор посимвольно → пауза с полным текстом → стирание посимвольно →
 * пауза с пустым полем → следующий плейсхолдер. Дойдя до конца массива, индекс обнуляется.
 */
export function getNextTypewriterStep(placeholders: string[], state: ITypewriterState): ITypewriterStep {
  const { phase, index, length } = state;
  const value = placeholders[index] ?? '';

  if (phase === 'typing') {
    if (length < value.length) {
      const next: ITypewriterState = { phase: 'typing', index, length: length + 1 };

      return { delayMs: TYPING_SPEED_MS, state: next, text: getTypewriterText(placeholders, next) };
    }

    // Текст набран целиком — держим его на виду и начинаем стирать.
    const next: ITypewriterState = { phase: 'erasing', index, length: Math.max(value.length - 1, 0) };

    return { delayMs: DELAY_BEFORE_ERASE_MS, state: next, text: getTypewriterText(placeholders, next) };
  }

  if (length > 0) {
    const next: ITypewriterState = { phase: 'erasing', index, length: length - 1 };

    return { delayMs: ERASING_SPEED_MS, state: next, text: getTypewriterText(placeholders, next) };
  }

  // Поле опустело — выдерживаем паузу и переходим к следующему плейсхолдеру по кругу.
  const next: ITypewriterState = {
    phase: 'typing',
    index: placeholders.length > 0 ? (index + 1) % placeholders.length : index,
    length: 0,
  };

  return { delayMs: DELAY_BEFORE_NEXT_MS, state: next, text: getTypewriterText(placeholders, next) };
}
